import React, { useState } from 'react';
import QrCode from '@/components/shared/QrCode';
import AddToAppleWalletButton from './AddToAppleWalletButton';
import { OrderWallet } from '@/lib/OrderApi';
import { Dialog, DialogContent, DialogTitle } from '@/components/shared/Dialog';
import { RiWalletLine } from '@remixicon/react';
import { Money } from '@/lib/money';
import { useLanguage } from '@/providers/LanguageProvider';
import * as m from "@/paraglide/messages.js"

interface WalletCardProps {
    wallet: OrderWallet;
}

const WalletCard: React.FC<WalletCardProps> = ({ wallet }) => {
    const [modalOpen, setModalOpen] = useState(false);
    const { currentLanguage: lang } = useLanguage();

    return (
        <div className="mt-4">
            <div className="flex min-h-[100px]">
                {/* Left Icon Section */}
                <div className="bg-[var(--shop-panelBackgroundColor)] text-[var(--shop-textColor)] w-[100px] shrink-0 flex flex-col items-center justify-center p-2 rounded-md rounded-tr-none rounded-br-none">
                    <button className='cursor-pointer flex flex-col items-center hover:scale-105 transition-all' onClick={() => setModalOpen(true)}>
                        <RiWalletLine className='h-12 py-2'/>
                        <span className='text-sm text-center tracking-tight'>{m.wallet()}</span>
                    </button>
                </div>
                {/* Divider SVG */}
                {/* Stretches with the card, so the perforation still reaches the bottom
                    when the amounts wrap to a second line. */}
                <svg width="10" height="100" viewBox="0 0 10 100" fill="none" preserveAspectRatio="none" xmlns="http://www.w3.org/2000/svg" className='w-[10px] shrink-0 self-stretch h-auto'>
                    <path fillRule="evenodd" clipRule="evenodd" d="M9.03223 100H8.89146C8.89146 97.5567 6.91399 95.576 4.47473 95.576C2.03548 95.576 0.0579977 97.5567 0.0579977 100H-0.000199318V0H0.0579977C0.0579977 2.44331 2.03548 4.42406 4.47473 4.42407C6.91399 4.42407 8.89146 2.44331 8.89146 0L9.03223 6.82303e-09V100ZM1.9747 86.8422C1.9747 85.4593 3.09405 84.3381 4.47473 84.3381C5.85541 84.3381 6.97476 85.4593 6.97476 86.8422C6.97476 88.2253 5.85541 89.3465 4.47473 89.3465C3.09405 89.3465 1.9747 88.2253 1.9747 86.8422ZM4.47473 75.5426C3.09405 75.5426 1.9747 76.6637 1.9747 78.0468C1.9747 79.4298 3.09405 80.551 4.47473 80.551C5.85541 80.551 6.97476 79.4298 6.97476 78.0468C6.97476 76.6637 5.85541 75.5426 4.47473 75.5426ZM1.9747 68.6978C1.9747 67.3148 3.09405 66.1937 4.47473 66.1937C5.85541 66.1937 6.97476 67.3148 6.97476 68.6978C6.97476 70.0809 5.85541 71.202 4.47473 71.202C3.09405 71.202 1.9747 70.0809 1.9747 68.6978ZM4.47473 56.8447C3.09405 56.8447 1.9747 57.966 1.9747 59.3489C1.9747 60.732 3.09405 61.8531 4.47473 61.8531C5.85541 61.8531 6.97476 60.732 6.97476 59.3489C6.97476 57.966 5.85541 56.8447 4.47473 56.8447ZM1.9747 50.0001C1.9747 48.617 3.09405 47.4959 4.47473 47.4959C5.85541 47.4959 6.97476 48.617 6.97476 50.0001C6.97476 51.383 5.85541 52.5041 4.47473 52.5041C3.09405 52.5041 1.9747 51.383 1.9747 50.0001ZM4.47473 38.1469C3.09405 38.1469 1.9747 39.2681 1.9747 40.6511C1.9747 42.0342 3.09405 43.1553 4.47473 43.1553C5.85541 43.1553 6.97476 42.0342 6.97476 40.6511C6.97476 39.2681 5.85541 38.1469 4.47473 38.1469ZM1.9747 31.3022C1.9747 29.9191 3.09405 28.798 4.47473 28.798C5.85541 28.798 6.97476 29.9191 6.97476 31.3022C6.97476 32.6852 5.85541 33.8064 4.47473 33.8064C3.09405 33.8064 1.9747 32.6852 1.9747 31.3022ZM4.47473 19.4492C3.09405 19.4492 1.9747 20.5703 1.9747 21.9532C1.9747 23.3363 3.09405 24.4574 4.47473 24.4574C5.85541 24.4574 6.97476 23.3363 6.97476 21.9532C6.97476 20.5703 5.85541 19.4492 4.47473 19.4492ZM1.9747 12.6044C1.9747 11.2213 3.09405 10.1002 4.47473 10.1002C5.85541 10.1002 6.97476 11.2213 6.97476 12.6044C6.97476 13.9873 5.85541 15.1085 4.47473 15.1085C3.09405 15.1085 1.9747 13.9873 1.9747 12.6044Z" fill="var(--shop-panelBackgroundColor)"/>
                </svg>
                {/* Right Content Section */}
                <div className="bg-[var(--shop-panelBackgroundColor)] text-[var(--shop-textColor)] grow -ml-[0.8px] flex flex-col justify-between rounded-md rounded-tl-none rounded-bl-none">
                    <div className='flex px-4 items-center justify-between border-[var(--shop-borderColor)] border-b grow'>
                        <span className='text-sm leading-normal font-semibold'>{m.wallet()}</span>
                    </div>
                    <div className='flex flex-wrap gap-x-2 gap-y-1 py-2 px-4 items-center justify-between grow'>
                        <div className='flex flex-col justify-center leading-tight min-w-0'>
                            {wallet.balance && (
                                <span className='text-base font-semibold truncate'>
                                    {m.wallet_balance({ amount: Money.fromInteger(wallet.balance.amount, wallet.balance.currency).display(lang) })}
                                </span>
                            )}
                            {wallet.originalBalance && (
                                <span className='text-[11px] opacity-70 truncate'>
                                    {m.wallet_loaded({ amount: Money.fromInteger(wallet.originalBalance.amount, wallet.originalBalance.currency).display(lang) })}
                                </span>
                            )}
                        </div>
                        <div className='flex items-center justify-end gap-2 sm:gap-4 grow'>
                            {wallet.walletUrl && (
                                <a
                                    href={wallet.walletUrl}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="flex items-center bg-[var(--shop-ctaButtonColor)] text-[var(--shop-ctaButtonTextColor)] font-bold p-2 rounded-md text-center hover:opacity-90 transition-all duration-300 hover:shadow-md"
                                >
                                    <RiWalletLine className="w-6 h-6" />
                                    <span className="ml-2 tracking-tight font-semibold hidden sm:inline">{m.wallet_go_to()}</span>
                                    <span className="ml-2 tracking-tight font-semibold sm:hidden">{m.wallet_open()}</span>
                                </a>
                            )}
                        </div>
                    </div>
                </div>
            </div>

            {/* Same as the ticket card: no room in the bottom row, so the pass badge goes under. */}
            <AddToAppleWalletButton walletId={wallet.walletId} className='h-[35px] mt-1' />

            <Dialog open={modalOpen} onOpenChange={setModalOpen}>
                <DialogContent className='bg-[var(--shop-panelBackgroundColor)] text-[var(--shop-textColor)]'>
                    <DialogTitle className='text-center mx-auto'>{m.wallet()}</DialogTitle>
                    <div className='text-center mx-auto'>
                        {wallet.balance && (
                            <p className='text-3xl font-bold'>
                                {Money.fromInteger(wallet.balance.amount, wallet.balance.currency).display(lang)}
                            </p>
                        )}
                        {wallet.originalBalance && (
                            <p className='text-sm opacity-70 mt-1'>
                                {m.wallet_loaded({ amount: Money.fromInteger(wallet.originalBalance.amount, wallet.originalBalance.currency).display(lang) })}
                            </p>
                        )}
                    </div>
                    {wallet.qrCode && (
                        <QrCode
                            size={256}
                            className='mx-auto max-w-[200px] p-4 bg-white my-6'
                            value={wallet.qrCode}
                        />
                    )}
                    <AddToAppleWalletButton walletId={wallet.walletId} />
                    {wallet.walletUrl && (
                        <a
                            href={wallet.walletUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="bg-[var(--shop-ctaButtonColor)] text-[var(--shop-ctaButtonTextColor)] font-bold p-2 rounded-md text-center block w-full hover:opacity-90 transition-all duration-300 hover:shadow-md mt-2"
                        >
                            <RiWalletLine className="w-5 h-5 inline-block mr-2 align-text-bottom" />
                            {m.wallet_go_to()}
                        </a>
                    )}
                </DialogContent>
            </Dialog>
        </div>
    );
};

export default WalletCard;
